import { useState, useRef, useCallback, useEffect, startTransition } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { SSE_ENDPOINT } from '../constants';
import { isPublicSharePath } from '@/constants/public-share-routes';
import { isImageLikeFile } from '@/lib/composer-clipboard';
import { objectUpdateQueryKeys } from '@/lib/object-update-query-keys';
import { isJwtExpired } from '../utils/jwt';
import type {
  ChatMessage,
  SSEEvent,
  ContentBlock,
  DeltaType,
  ToolLifecycleStatus,
  ThreadMessageAttachment,
  ObjectUpdateItem,
  NavigationIntentMobileBehavior,
  ChatSuggestion,
  ChatSuggestionsPayload,
  SuggestionChipState,
} from '../types';
import { objectUpdateToItems, rawToObjectUpdateItem } from '../utils/object-update-utils';
import type { NavigateFunction } from 'react-router-dom';
import { mapThreadSseEvent } from '../threading/sse-adapter';
import { compareActivity, isActivityMessage } from '../threading/turn-order';
import { isDuplicateNavigationTarget, shouldApplyNavigationDelta } from '../threading/sse-navigation-guard';
import { appendDiscourseSupportNote } from '../threading/discourse-support-note';
import { shouldProcessCapabilityDelta } from '../threading/sse-capability-guard';
import { PERSISTENT_OPEN_WHEN_HIDDEN } from '../threading/persistent-sse';
import {
  clampNavigationDelay,
  employerAgentSessionContext,
  normalizeNavigationMobileBehavior,
  shouldAutoOpenNavigationIntent,
} from '../threading/navigation-intent';
import { toast } from 'sonner';
import {
  buildSuggestionsCursorKey,
  cursorKeyFromPayload,
} from '../lib/chat-suggestions-cursor';
import {
  applySuggestionStreamEvent,
  type SuggestionStreamHandlers,
} from '../lib/suggestion-stream-events';
import {
  applyCapabilityMenuShells,
  applyCapabilityMenuStreamEvent,
  type CapabilityMenuStreamHandlers,
} from '../lib/capability-menu-stream-events';
import {
  dedupeInThreadSuggestionRows,
  inThreadSuggestionMessageId,
  mergeSuggestionChipsFromStop,
  preferLongerText,
  removeInThreadSuggestionRow,
  upsertSuggestionRowAfterAssistant,
} from '../lib/suggestion-message-order';

/**
 * useSSE Hook - Handles both chat messages and persistent real-time connections
 *
 * @param onMessageStart - Callback when AI starts responding (chat mode only)
 * @param token - JWT authentication token
 * @param autoConnect - If true, establishes persistent connection on mount for real-time features
 * @param threadCallbacks - Optional callbacks for thread-related delta events
 *
 * @returns Object with connection state, chat state, and actions
 *
 * Usage:
 * // Chat only (default)
 * const sse = useSSE(onMessageStart, token);
 *
 * // With persistent connection for real-time features
 * const sse = useSSE(onMessageStart, token, true);
 *
 * // With thread event callbacks
 * const sse = useSSE(onMessageStart, token, true, { onThreadCreated, onThreadUpdated, onMessageSaved });
 */
export const useSSE = (
  onMessageStart?: () => void, 
  token?: string | null, 
  autoConnect: boolean = false, 
  threadCallbacks?: { 
    onThreadCreated?: (data: any) => void; 
    onThreadUpdated?: (data: any) => void; 
    onThreadTitleGenerating?: (data: any) => void; 
    onMessageSaved?: (data: any) => void; 
    onUIEvent?: (data: any) => void;
    onThreadEvent?: (event: {
      kind: 'thread_created' | 'thread_updated' | 'thread_title_updated' | 'thread_summary_updated'
      threadId: string
      subject?: string
      summary?: string
      updates_digest?: string[]
    }) => void;
  },
  searchCallbacks?: {
    onSearchThinking?: (data: any) => void;
    onSearchProgress?: (data: any) => void;
    onSearchCandidate?: (data: any) => void;
    onSearchComplete?: (data: any) => void;
  }, 
  currentThreadId?: string | null,
  navigationHelpers?: {
    navigate?: NavigateFunction;
    openRoute?: (path: string) => void;
    navigateInPlace?: (path: string) => void;
    isMobile?: boolean;
    queryClient?: any; // React Query QueryClient
    searchParams?: URLSearchParams;
    onAuthFailure?: () => void; // Called on 401; clears auth and stops reconnect
    /** Create a thread when none exists (e.g. before first file upload). */
    ensureThread?: () => Promise<string | null>;
  }
) => {
  // Core state
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    messagesSnapshotRef.current = messages;
  }, [messages]);
  const [stagedAttachments, setStagedAttachments] = useState<ThreadMessageAttachment[]>([]);
  const stagedAttachmentsRef = useRef<ThreadMessageAttachment[]>([]);
  /** Thread id used for last successful upload; avoids missing thread_id on /agent/stream before React applies selectThread. */
  const lastAttachmentThreadIdRef = useRef<string | null>(null);
  const [isConnected, setIsConnected] = useState(false); // Message stream connection state
  const [isPersistentConnected, setIsPersistentConnected] = useState(false); // Persistent connection state
  const isPersistentConnectedRef = useRef(false);
  const setPersistentConnected = (connected: boolean) => {
    isPersistentConnectedRef.current = connected;
    setIsPersistentConnected(connected);
  };
  const [isLoading, setIsLoading] = useState(false);
  const [chatSuggestions, setChatSuggestions] = useState<ChatSuggestion[]>([]);
  const [isChatSuggestionsLoading, setIsChatSuggestionsLoading] = useState(false);
  const [isAwaitingChatSuggestions, setIsAwaitingChatSuggestions] = useState(false);
  
  // Track recently added navigation/update messages to prevent duplicates
  const recentNavMessagesRef = useRef<Map<string, number>>(new Map());
  const lastNavigationRef = useRef<{ target: string; at: number } | null>(null);
  const navigationTimersRef = useRef<Map<string, number>>(new Map());
  const messageSendInFlightRef = useRef<boolean>(false);
  const messageSendFailsafeTimeoutRef = useRef<number | null>(null);
  const satisfiedSuggestionsKeysRef = useRef<Set<string>>(new Set());
  const suggestionsAwaitTimeoutRef = useRef<number | null>(null);
  const pendingInThreadSuggestionsRef = useRef<{
    runId: string;
    chips: SuggestionChipState[];
    cursor?: ChatSuggestionsPayload['cursor'];
    finalized?: boolean;
  } | null>(null);
  const DEFAULT_SUGGESTION_SLOTS = 4;
  const suppressCapabilityUpdatesUntilNextSendRef = useRef<boolean>(false);
  const llmProviderNoticeDedupeRef = useRef<Set<string>>(new Set());
  const authFailureRef = useRef<boolean>(false);
  const hydrateSuggestionsAbortRef = useRef<AbortController | null>(null);
  const messagesSnapshotRef = useRef<ChatMessage[]>([]);
  const suggestionStreamHandlersRef = useRef<SuggestionStreamHandlers | null>(null);

  // Thread management (received from parent)
  const effectiveCurrentThreadId = currentThreadId;

  // Token ref
  const tokenRef = useRef<string | null>(null);

  // Update token ref when token changes; reset auth failure flag on fresh token (e.g. after re-login)
  useEffect(() => {
    tokenRef.current = token || null;
    if (token) authFailureRef.current = false;
  }, [token]);

  const onAuthFailure = navigationHelpers?.onAuthFailure;
  /** Only logout when 401 applies to the active JWT (ignore stale in-flight SSE attempts). */
  const handleAuthFailureForToken = useCallback((connectionToken: string | null | undefined) => {
    if (connectionToken && tokenRef.current !== connectionToken) {
      return;
    }
    authFailureRef.current = true;
    onAuthFailure?.();
    if (
      !isPublicSharePath(window.location.pathname) &&
      window.location.pathname !== '/' &&
      !window.location.pathname.startsWith('/login')
    ) {
      window.location.href = '/login';
    }
  }, [onAuthFailure]);
  const handleAuthFailure = useCallback(() => {
    handleAuthFailureForToken(tokenRef.current);
  }, [handleAuthFailureForToken]);

  // Debug when currentThreadId changes
  useEffect(() => {
    console.log('🚨 [USE SSE] currentThreadId changed to:', currentThreadId, 'effectiveCurrentThreadId:', effectiveCurrentThreadId);
  }, [currentThreadId, effectiveCurrentThreadId]);

  // Debug flag for verbose SSE logging
  const DEBUG_SSE = import.meta.env.VITE_DEBUG_SSE === 'true' || import.meta.env.DEV;

  const [agentCapabilities, setAgentCapabilities] = useState<string[]>([]);
  const [capabilityMenuCategories, setCapabilityMenuCategories] = useState<
    import('@/types/composer').ComposerCapabilityCategory[]
  >([]);
  const capabilityMenuRevisionRef = useRef<string | null>(null);
  // Source of truth for capability menu state during streaming. Events mutate the
  // ref synchronously (preserving ordering); React state is published via a
  // rAF-batched, low-priority flush so per-token deltas don't re-render per event.
  const capabilityMenuCategoriesRef = useRef(capabilityMenuCategories);
  const capabilityMenuFlushRafRef = useRef(0);

  const flushCapabilityMenu = useCallback(() => {
    if (capabilityMenuFlushRafRef.current) {
      cancelAnimationFrame(capabilityMenuFlushRafRef.current);
      capabilityMenuFlushRafRef.current = 0;
    }
    startTransition(() => {
      setCapabilityMenuCategories(capabilityMenuCategoriesRef.current);
    });
  }, []);

  const scheduleCapabilityMenuFlush = useCallback(() => {
    if (capabilityMenuFlushRafRef.current) return;
    capabilityMenuFlushRafRef.current = requestAnimationFrame(() => {
      capabilityMenuFlushRafRef.current = 0;
      flushCapabilityMenu();
    });
  }, [flushCapabilityMenu]);

  useEffect(() => () => {
    if (capabilityMenuFlushRafRef.current) {
      cancelAnimationFrame(capabilityMenuFlushRafRef.current);
      capabilityMenuFlushRafRef.current = 0;
    }
  }, []);

  // Per-run anchor tracking (runId -> assistant message id)
  const runAnchorsRef = useRef<Record<string, string>>({});

  // Active thread ref so in-flight stream handlers (with stale closures) can drop
  // events for threads the user has switched away from.
  const currentThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId ? String(currentThreadId) : null;
  }, [currentThreadId]);

  // SSE management - separate controllers for persistent and message streams
  const persistentAbortControllerRef = useRef<AbortController | null>(null);
  const establishPersistentConnectionRef = useRef<(() => Promise<void>) | null>(null);
  const messageAbortControllerRef = useRef<AbortController | null>(null);
  const contentBlocksRef = useRef<Record<number, ContentBlock>>({});
  const currentRunIdRef = useRef<string | null>(null);
  const handleSSEEventRef = useRef<((event: SSEEvent) => void) | null>(null);

  const messageStreamActiveRef = useRef<boolean>(false); // Track if message stream is active (from sendMessage to message_stop)

  // Track visibility for throttling agent_delta on tab return (avoids capabilities reload)
  // Only set suppression when returning from hidden; avoid suppressing initial page load summaries.
  const lastVisibleAtRef = useRef<number>(0);
  const wasHiddenRef = useRef<boolean>(false);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastVisibleAtRef.current = Date.now();
        if (wasHiddenRef.current) {
          suppressCapabilityUpdatesUntilNextSendRef.current = true;
        }
        wasHiddenRef.current = false;
      } else if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []); // Track blocks that have received stop events

  const resetMessageSendState = useCallback(() => {
    messageStreamActiveRef.current = false;
    messageSendInFlightRef.current = false;
    if (messageSendFailsafeTimeoutRef.current !== null) {
      window.clearTimeout(messageSendFailsafeTimeoutRef.current);
      messageSendFailsafeTimeoutRef.current = null;
    }
  }, []);

  // Utility function
  const generateId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  const clearSuggestionsAwaitTimeout = useCallback(() => {
    if (suggestionsAwaitTimeoutRef.current !== null) {
      window.clearTimeout(suggestionsAwaitTimeoutRef.current);
      suggestionsAwaitTimeoutRef.current = null;
    }
  }, []);

  const scheduleSuggestionsAwaitTimeout = useCallback(() => {
    clearSuggestionsAwaitTimeout();
    suggestionsAwaitTimeoutRef.current = window.setTimeout(() => {
      suggestionsAwaitTimeoutRef.current = null;
      setIsAwaitingChatSuggestions(false);
    }, 18000);
  }, [clearSuggestionsAwaitTimeout]);

  const clearChatSuggestions = useCallback(() => {
    satisfiedSuggestionsKeysRef.current.clear();
    clearSuggestionsAwaitTimeout();
    setIsAwaitingChatSuggestions(false);
    setChatSuggestions([]);
    hydrateSuggestionsAbortRef.current?.abort();
    hydrateSuggestionsAbortRef.current = null;
    setMessages((prev) => prev.filter((m) => m.messageType !== 'chat_suggestions'));
  }, [clearSuggestionsAwaitTimeout]);

  const applyChatSuggestionsPayload = useCallback((payload: ChatSuggestionsPayload) => {
    const payloadThreadId = payload.cursor?.thread_id;
    if (payloadThreadId && effectiveCurrentThreadId && payloadThreadId !== effectiveCurrentThreadId) {
      console.log('[CHAT SUGGESTIONS] Ignoring stale suggestions for thread:', payloadThreadId);
      return;
    }
    const key = cursorKeyFromPayload(payload);
    if (key) satisfiedSuggestionsKeysRef.current.add(key);
    clearSuggestionsAwaitTimeout();
    setIsAwaitingChatSuggestions(false);
    setChatSuggestions([]);
    const runId = payload.cursor?.turn_run_id || currentRunIdRef.current || 'unknown';
    const chips: SuggestionChipState[] = (payload.suggestions || []).map((s) => ({
      id: s.id,
      label: s.label,
      message: s.message,
      labelComplete: true,
      messageComplete: true,
      clickable: true,
    }));
    if (!chips.length) return;
    setMessages((prev) => {
      if (!runAnchorsRef.current[runId]) {
        const anchor = [...prev].reverse().find(
          (m) => !m.isUser && !m.isDelta && m.runId === runId,
        );
        if (anchor) runAnchorsRef.current[runId] = anchor.id;
      }
      const msgId = inThreadSuggestionMessageId(runId);
      const row: ChatMessage = {
        id: msgId,
        content: '',
        isUser: false,
        runId,
        messageType: 'chat_suggestions',
        suggestionChips: chips,
        suggestionsCursor: payload.cursor,
        finalizedAt: Date.now(),
      };
      if (prev.some((m) => m.id === msgId)) {
        return dedupeInThreadSuggestionRows(
          prev.map((m) => {
            if (m.id !== msgId) return m;
            const { isDelta: _d, deltaType: _t, ...base } = m;
            return { ...base, ...row };
          }),
          runId,
        );
      }
      const anchorId = runAnchorsRef.current[runId];
      const anchorIdx = anchorId ? prev.findIndex((m) => m.id === anchorId) : -1;
      let next: ChatMessage[];
      if (anchorIdx >= 0) {
        next = [...prev.slice(0, anchorIdx + 1), row, ...prev.slice(anchorIdx + 1)];
      } else {
        next = [...prev, row];
      }
      return dedupeInThreadSuggestionRows(next, runId);
    });
  }, [effectiveCurrentThreadId, clearSuggestionsAwaitTimeout]);

  const refreshChatSuggestions = useCallback(async (
    threadId: string,
    mode: 'bootstrap' | 'hydrate' | 'after_turn' = 'hydrate',
    options?: {
      afterAssistantMessageId?: string;
      turnRunId?: string | null;
      maxSuggestions?: number;
      anchorMessageId?: string;
    }
  ): Promise<boolean> => {
    if (!threadId || authFailureRef.current) return false;

    if (mode === 'hydrate') {
      hydrateSuggestionsAbortRef.current?.abort();
      const abortController = new AbortController();
      hydrateSuggestionsAbortRef.current = abortController;
      setIsChatSuggestionsLoading(true);
      let streamRunId = options?.turnRunId || `hydrate_${options?.afterAssistantMessageId || 'unknown'}`;
      if (options?.anchorMessageId && streamRunId) {
        runAnchorsRef.current[streamRunId] = options.anchorMessageId;
      }
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        };
        if (tokenRef.current) headers.Authorization = `Bearer ${tokenRef.current}`;
        const handlers = suggestionStreamHandlersRef.current;
        if (!handlers) return false;
        await fetchEventSource(`/api/threads/${threadId}/suggestions/stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            mode,
            after_assistant_message_id: options?.afterAssistantMessageId,
            turn_run_id: options?.turnRunId,
            max_suggestions: options?.maxSuggestions,
          }),
          signal: abortController.signal,
          openWhenHidden: true,
          onmessage(ev) {
            if (!ev.data) return;
            let data: Record<string, unknown>;
            try {
              data = JSON.parse(ev.data) as Record<string, unknown>;
            } catch {
              return;
            }
            const eventType = ev.event || (data.type as string) || 'message';
            if (data.runId) streamRunId = String(data.runId);
            applySuggestionStreamEvent(eventType, data, handlers);
          },
        });
        clearSuggestionsAwaitTimeout();
        setIsAwaitingChatSuggestions(false);
        return true;
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return false;
        console.warn('[CHAT SUGGESTIONS] Stream hydrate error:', error);
        return false;
      } finally {
        setIsChatSuggestionsLoading(false);
        if (hydrateSuggestionsAbortRef.current === abortController) {
          hydrateSuggestionsAbortRef.current = null;
        }
      }
    }

    setIsChatSuggestionsLoading(true);
    let applied = false;
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (tokenRef.current) headers.Authorization = `Bearer ${tokenRef.current}`;
      const requestToken = tokenRef.current;
      const response = await fetch(`/api/threads/${threadId}/suggestions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          mode,
          after_assistant_message_id: options?.afterAssistantMessageId,
          turn_run_id: options?.turnRunId,
          max_suggestions: options?.maxSuggestions,
        }),
      });
      if (response.status === 401) {
        handleAuthFailureForToken(requestToken);
        return false;
      }
      if (!response.ok) {
        console.warn('[CHAT SUGGESTIONS] Request failed:', response.status);
        return false;
      }
      const payload = (await response.json()) as ChatSuggestionsPayload;
      applyChatSuggestionsPayload(payload);
      applied = true;
      return true;
    } catch (error) {
      console.warn('[CHAT SUGGESTIONS] Request error:', error);
      return false;
    } finally {
      setIsChatSuggestionsLoading(false);
      if (!applied) {
        clearSuggestionsAwaitTimeout();
        setIsAwaitingChatSuggestions(false);
      }
    }
  }, [
    applyChatSuggestionsPayload,
    clearSuggestionsAwaitTimeout,
    handleAuthFailureForToken,
  ]);

  const loadSuggestionsForThread = useCallback(async (
    threadId: string,
    latestAssistant: { serverMessageId: string; runId?: string | null; anchorMessageId?: string },
  ): Promise<boolean> => {
    if (!threadId || authFailureRef.current) return false;
    const key = buildSuggestionsCursorKey(
      threadId,
      latestAssistant.serverMessageId,
      latestAssistant.runId ?? undefined,
    );
    if (satisfiedSuggestionsKeysRef.current.has(key)) return true;
    setIsAwaitingChatSuggestions(true);
    scheduleSuggestionsAwaitTimeout();
    return refreshChatSuggestions(threadId, 'hydrate', {
      afterAssistantMessageId: latestAssistant.serverMessageId,
      turnRunId: latestAssistant.runId ?? null,
      anchorMessageId: latestAssistant.anchorMessageId,
    });
  }, [refreshChatSuggestions, scheduleSuggestionsAwaitTimeout]);

  /**
   * Create a delta message with required fields.
   * This ensures all delta messages have proper deltaType and required fields.
   * 
   * @param deltaType - Required delta type (memory, tool, voice, system, navigation, content_update, content_refresh)
   * @param content - Message content
   * @param options - Optional fields (id, runId, finalizedAt)
   * @returns Properly typed ChatMessage with isDelta: true
   */
  const createDeltaMessage = (
    deltaType: DeltaType,
    content: string,
    options?: {
      id?: string;
      runId?: string;
      finalizedAt?: number;
      orderSeq?: number;
      toolRound?: number;
      toolCallIndex?: number;
      toolName?: string;
      toolLabel?: string;
      toolStatus?: ToolLifecycleStatus;
      memorySubType?: 'retrieved' | 'stored';
      memoryLabel?: string;
      memoryCount?: number;
    }
  ): ChatMessage => {
    const now = Date.now();
    return {
      id: options?.id || generateId(),
      content,
      isUser: false,
      runId: options?.runId || currentRunIdRef.current || 'unknown',
      isDelta: true,
      deltaType,
      createdAt: now,
      seq: now,
      finalizedAt: options?.finalizedAt,
      orderSeq: options?.orderSeq,
      toolRound: options?.toolRound,
      toolCallIndex: options?.toolCallIndex,
      toolName: options?.toolName,
      toolLabel: options?.toolLabel,
      toolStatus: options?.toolStatus,
      memorySubType: options?.memorySubType,
      memoryLabel: options?.memoryLabel,
      memoryCount: options?.memoryCount,
    };
  };

  const memoryInsertPosition = (subType?: 'retrieved' | 'stored'): 'pre' | 'post' =>
    subType === 'stored' ? 'post' : 'pre';

  const ensureMemoryDeltaMessageForBlock = (
    messages: ChatMessage[],
    runId: string,
    block: ContentBlock,
    orderSeq?: number,
  ): ChatMessage[] => {
    const exists = messages.some(m => m.id === block.id);
    if (exists) return messages;
    const newDelta = createDeltaMessage('memory', block.content || '', {
      id: block.id,
      runId,
      orderSeq,
      memorySubType: block.memory_sub_type,
      memoryLabel: block.memory_label,
      memoryCount: block.memory_count,
    });
    return insertDelta(messages, runId, newDelta, memoryInsertPosition(block.memory_sub_type));
  };

  const clearNavigationTimer = useCallback((messageId: string) => {
    const timer = navigationTimersRef.current.get(messageId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      navigationTimersRef.current.delete(messageId);
    }
  }, []);

  const clearAllNavigationTimers = useCallback(() => {
    navigationTimersRef.current.forEach(timer => window.clearTimeout(timer));
    navigationTimersRef.current.clear();
  }, []);

  useEffect(() => clearAllNavigationTimers, [clearAllNavigationTimers]);

  const performNavigationIntent = useCallback((
    messageId: string,
    targetPath: string,
    mobileBehavior: NavigationIntentMobileBehavior,
    forceSwitch: boolean = false
  ) => {
    clearNavigationTimer(messageId);
    const isMobile = !!navigationHelpers?.isMobile;
    const behavior = forceSwitch ? 'switch' : mobileBehavior;

    if (isMobile && behavior === 'switch' && navigationHelpers?.openRoute) {
      navigationHelpers.openRoute(targetPath);
    } else if (isMobile && behavior === 'stay' && navigationHelpers?.navigateInPlace) {
      navigationHelpers.navigateInPlace(targetPath);
    } else if (navigationHelpers?.navigate && navigationHelpers?.searchParams) {
      import('../utils/thread-navigation').then(({ navigateWithThreadPreservation }) => {
        navigateWithThreadPreservation(
          navigationHelpers.navigate!,
          targetPath,
          navigationHelpers.searchParams!
        );
      }).catch(() => {});
    }

    setMessages(prev => prev.map(msg =>
      msg.id === messageId && msg.messageType === 'navigation_intent' && msg.navigationIntent
        ? {
            ...msg,
            navigationIntent: {
              ...msg.navigationIntent,
              status: 'opened',
            },
          }
        : msg
    ));
  }, [clearNavigationTimer, navigationHelpers]);

  const cancelNavigationIntent = useCallback((messageId: string) => {
    clearNavigationTimer(messageId);
    setMessages(prev => prev.map(msg =>
      msg.id === messageId && msg.messageType === 'navigation_intent' && msg.navigationIntent
        ? {
            ...msg,
            navigationIntent: {
              ...msg.navigationIntent,
              status: 'cancelled',
              opensAt: undefined,
            },
          }
        : msg
    ));
  }, [clearNavigationTimer]);

  const scheduleNavigationIntent = useCallback((message: ChatMessage) => {
    const intent = message.navigationIntent;
    if (!intent || !intent.target_path || intent.status !== 'pending') return;
    if (navigationTimersRef.current.has(message.id)) {
      clearNavigationTimer(message.id);
    }
    const delay = Math.max(0, (intent.opensAt ?? Date.now()) - Date.now());
    const timer = window.setTimeout(() => {
      performNavigationIntent(
        message.id,
        intent.target_path,
        intent.mobile_behavior ?? 'prompt'
      );
    }, delay);
    navigationTimersRef.current.set(message.id, timer);
  }, [clearNavigationTimer, performNavigationIntent]);

  // Insert delta message at correct position relative to assistant message
  // (ordering authority lives in threading/turn-order.ts)
  const insertDelta = (
    messages: ChatMessage[],
    runId: string,
    newDelta: ChatMessage,
    placement: 'pre' | 'post'
  ): ChatMessage[] => {
    const isRunGroupedMessage = (m: ChatMessage, rid: string) => {
      if (m.runId !== rid) return false;
      if (m.isDelta) return true;
      return m.messageType === 'navigation_intent' || m.messageType === 'chat_suggestions' || m.messageType === 'object_update';
    };

    if (placement === 'post') {
      // Ambient rows (memory stored, etc.): append after the anchor and any
      // run-grouped rows already following it. Never reorders existing rows.
      const anchorId = runAnchorsRef.current[runId];
      const anchorIdx = anchorId ? messages.findIndex(m => m.id === anchorId) : -1;
      if (anchorIdx === -1) return [...messages, newDelta];
      let idx = anchorIdx + 1;
      while (idx < messages.length && isRunGroupedMessage(messages[idx], runId)) idx++;
      return [...messages.slice(0, idx), newDelta, ...messages.slice(idx)];
    }

    // Pre-anchor activity cluster: rebuild sorted by logical key.
    const cluster: ChatMessage[] = [];
    const rest: ChatMessage[] = [];
    for (const m of messages) {
      (isActivityMessage(m, runId) ? cluster : rest).push(m);
    }
    cluster.push(newDelta);
    cluster.sort(compareActivity);

    const anchorId = runAnchorsRef.current[runId];
    const anchorIdx = anchorId ? rest.findIndex(m => m.id === anchorId) : -1;
    if (anchorIdx === -1) {
      // No anchor yet - cluster trails the conversation until the text block lands.
      return [...rest, ...cluster];
    }
    return [...rest.slice(0, anchorIdx), ...cluster, ...rest.slice(anchorIdx)];
  };

  const emptySuggestionSlots = (count: number): SuggestionChipState[] =>
    Array.from({ length: count }, () => ({
      label: '',
      message: '',
      clickable: false,
    }));

  const insertInThreadSuggestionRow = (
    prev: ChatMessage[],
    runId: string,
    row: ChatMessage,
    pendingChips?: SuggestionChipState[],
  ): ChatMessage[] => {
    const anchorId = runAnchorsRef.current[runId];
    if (!anchorId) {
      pendingInThreadSuggestionsRef.current = {
        runId,
        chips: pendingChips ?? row.suggestionChips ?? [],
        cursor: row.suggestionsCursor,
        finalized: Boolean(row.finalizedAt),
      };
      const placed = upsertSuggestionRowAfterAssistant(prev, row, null);
      return dedupeInThreadSuggestionRows(placed, runId);
    }
    pendingInThreadSuggestionsRef.current = null;
    const placed = upsertSuggestionRowAfterAssistant(prev, row, anchorId);
    return dedupeInThreadSuggestionRows(placed, runId);
  };

  const upsertInThreadSuggestions = (
    runId: string,
    chips: SuggestionChipState[],
    cursor?: ChatSuggestionsPayload['cursor'],
    finalized?: boolean,
  ) => {
    const msgId = inThreadSuggestionMessageId(runId);
    setMessages((prev) => {
      const row: ChatMessage = {
        id: msgId,
        content: '',
        isUser: false,
        runId,
        messageType: 'chat_suggestions',
        suggestionChips: chips,
        suggestionsCursor: cursor,
        ...(finalized ? { finalizedAt: Date.now() } : {}),
      };
      return insertInThreadSuggestionRow(prev, runId, row, chips);
    });
  };

  const flushPendingInThreadSuggestions = () => {
    let pending = pendingInThreadSuggestionsRef.current;
    if (!pending) return;

    const effectiveRunId = currentRunIdRef.current ?? pending.runId;
    if (pending.runId !== effectiveRunId) {
      pending = { ...pending, runId: effectiveRunId };
      pendingInThreadSuggestionsRef.current = pending;
    }

    const anchor =
      runAnchorsRef.current[pending.runId] ??
      (currentRunIdRef.current
        ? runAnchorsRef.current[currentRunIdRef.current]
        : undefined);
    if (!anchor) return;

    pendingInThreadSuggestionsRef.current = null;
    upsertInThreadSuggestions(
      pending.runId,
      pending.chips,
      pending.cursor,
      pending.finalized,
    );
  };

  const applySuggestionChipDelta = (
    runId: string,
    chipIndex: number,
    field: 'label' | 'message',
    text: string,
  ) => {
    const msgId = inThreadSuggestionMessageId(runId);
    setMessages((prev) => {
      let base = prev;
      if (!prev.some((m) => m.id === msgId)) {
        const row: ChatMessage = {
          id: msgId,
          content: '',
          isUser: false,
          runId,
          messageType: 'chat_suggestions',
          suggestionChips: emptySuggestionSlots(DEFAULT_SUGGESTION_SLOTS),
        };
        base = insertInThreadSuggestionRow(prev, runId, row);
      }
      return dedupeInThreadSuggestionRows(
        base.map((m) => {
          if (m.id !== msgId || !m.suggestionChips) return m;
          const chips = [...m.suggestionChips];
          while (chips.length <= chipIndex) {
            chips.push({ label: '', message: '', clickable: false });
          }
          const chip = { ...chips[chipIndex] };
          if (field === 'label') chip.label = (chip.label || '') + text;
          if (field === 'message') chip.message = (chip.message || '') + text;
          chips[chipIndex] = chip;
          return { ...m, suggestionChips: chips };
        }),
        runId,
      );
    });
  };

  const applySuggestionChipReady = (
    runId: string,
    chipIndex: number,
    chip: { id: string; label: string; message: string },
  ) => {
    const msgId = inThreadSuggestionMessageId(runId);
    setMessages((prev) => {
      let base = prev;
      if (!prev.some((m) => m.id === msgId)) {
        const row: ChatMessage = {
          id: msgId,
          content: '',
          isUser: false,
          runId,
          messageType: 'chat_suggestions',
          suggestionChips: emptySuggestionSlots(DEFAULT_SUGGESTION_SLOTS),
        };
        base = insertInThreadSuggestionRow(prev, runId, row);
      }
      return dedupeInThreadSuggestionRows(
        base.map((m) => {
          if (m.id !== msgId || !m.suggestionChips) return m;
          const chips = [...m.suggestionChips];
          while (chips.length <= chipIndex) {
            chips.push({ label: '', message: '', clickable: false });
          }
          chips[chipIndex] = {
            id: chip.id,
            label: preferLongerText(chips[chipIndex]?.label, chip.label),
            message: preferLongerText(chips[chipIndex]?.message, chip.message),
            labelComplete: true,
            messageComplete: true,
            clickable: true,
          };
          return { ...m, suggestionChips: chips };
        }),
        runId,
      );
    });
  };

  const patchSuggestionStopCursor = useCallback(
    (
      runId: string,
      cursor: ChatSuggestionsPayload['cursor'] | undefined,
      suggestions: ChatSuggestionsPayload['suggestions'],
    ) => {
      const msgId = inThreadSuggestionMessageId(runId);
      setMessages((prev) =>
        dedupeInThreadSuggestionRows(
          prev.map((m) => {
            if (m.id !== msgId || !m.suggestionChips) return m;
            const chips = mergeSuggestionChipsFromStop(m.suggestionChips, suggestions ?? []);
            return {
              ...m,
              suggestionChips: chips,
              ...(cursor ? { suggestionsCursor: cursor } : {}),
              finalizedAt: Date.now(),
            };
          }),
          runId,
        ),
      );
    },
    [],
  );

  const clearInThreadSuggestions = useCallback((runId: string) => {
    if (pendingInThreadSuggestionsRef.current?.runId === runId) {
      pendingInThreadSuggestionsRef.current = null;
    }
    setMessages((prev) => removeInThreadSuggestionRow(prev, runId));
  }, []);

  const getSuggestionStreamHandlers = useCallback((): SuggestionStreamHandlers => ({
    defaultSlotCount: DEFAULT_SUGGESTION_SLOTS,
    emptySlots: emptySuggestionSlots,
    upsertInThread: upsertInThreadSuggestions,
    applyDelta: applySuggestionChipDelta,
    applyReady: applySuggestionChipReady,
    patchStopCursor: patchSuggestionStopCursor,
    recordSatisfiedCursor: (payload) => {
      const key = cursorKeyFromPayload(payload);
      if (key) satisfiedSuggestionsKeysRef.current.add(key);
    },
    hasProgressiveChips: (runId) => {
      const msgId = inThreadSuggestionMessageId(runId);
      const row = messagesSnapshotRef.current.find((m) => m.id === msgId);
      return Boolean(
        row?.suggestionChips?.some((c) => c.label || c.id || c.clickable),
      );
    },
    clearInThread: clearInThreadSuggestions,
  }), [patchSuggestionStopCursor, clearInThreadSuggestions]);

  suggestionStreamHandlersRef.current = getSuggestionStreamHandlers();

  const getCapabilityMenuStreamHandlers = useCallback((): CapabilityMenuStreamHandlers => ({
    getState: () => ({
      menuRevision: capabilityMenuRevisionRef.current,
      categories: capabilityMenuCategoriesRef.current,
    }),
    setState: (next) => {
      capabilityMenuRevisionRef.current = next.menuRevision;
      capabilityMenuCategoriesRef.current = next.categories;
      scheduleCapabilityMenuFlush();
    },
  }), [scheduleCapabilityMenuFlush]);

  // Ensure a durable delta message exists for this block; create if missing
  const ensureDeltaMessageForBlock = (
    messages: ChatMessage[],
    runId: string,
    block: ContentBlock,
    deltaType: DeltaType,
    orderSeq?: number
  ): ChatMessage[] => {
    const exists = messages.some(m => m.id === block.id);
    if (exists) return messages;
    const newDelta = createDeltaMessage(deltaType, block.content || '', {
      id: block.id,
      runId,
      orderSeq,
      toolRound: block.round,
      toolCallIndex: block.call_index,
      toolName: block.tool_name,
      toolLabel: block.tool_label,
      ...(deltaType === 'tool' ? { toolStatus: 'running' as ToolLifecycleStatus } : {}),
    });
    return insertDelta(messages, runId, newDelta, 'pre');
  };

  const appendObjectUpdateItemsToRunGroup = (
    messages: ChatMessage[],
    runId: string,
    itemsToAdd: ObjectUpdateItem[],
    orderSeq?: number
  ): ChatMessage[] => {
    if (!itemsToAdd.length) return messages;

    let idx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.messageType === 'object_update' && m.runId === runId) {
        idx = i;
        break;
      }
    }

    const mergeEnvelope = (
      prevOu: ChatMessage['objectUpdate'] | undefined,
      additions: ObjectUpdateItem[]
    ): ChatMessage['objectUpdate'] => {
      const existing = objectUpdateToItems(prevOu);
      return { items: [...existing, ...additions] };
    };

    if (idx === -1) {
      const newMsg = {
        id: generateId(),
        content: '',
        isUser: false,
        runId,
        messageType: 'object_update',
        orderSeq,
        objectUpdate: { items: [...itemsToAdd] },
      } as ChatMessage;
      return insertDelta(messages, runId, newMsg, 'pre');
    }

    const existingMessage = messages[idx];
    const mergedMessage: ChatMessage = {
      ...existingMessage,
      runId: existingMessage.runId || runId,
      messageType: 'object_update',
      objectUpdate: mergeEnvelope(existingMessage.objectUpdate, itemsToAdd),
    };
    return messages.map((msg, i) => (i === idx ? mergedMessage : msg));
  };

  // Establish persistent connection for real-time features
  const establishPersistentConnection = useCallback(async () => {
    if (!tokenRef.current || isPersistentConnectedRef.current || authFailureRef.current) return;

    const connectionToken = tokenRef.current;
    const abortController = new AbortController();
    persistentAbortControllerRef.current = abortController;

    try {
      console.log('[SSE] Establishing persistent connection...');

      const threadFromUrl =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('thread')
          : null;
      const threadIdForFocus =
        effectiveCurrentThreadId || (threadFromUrl ? String(threadFromUrl) : null);

      await fetchEventSource(SSE_ENDPOINT, {
        method: 'POST',
        openWhenHidden: PERSISTENT_OPEN_WHEN_HIDDEN,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(connectionToken && { 'Authorization': `Bearer ${connectionToken}` }),
        },
        body: JSON.stringify({
          connect_only: true,
          ...employerAgentSessionContext(),
          ...(threadIdForFocus ? { thread_id: threadIdForFocus } : {}),
        }),
        signal: abortController.signal,

        async onopen(response) {
          if (!response.ok) {
            if (response.status === 401) {
              handleAuthFailureForToken(connectionToken);
            }
            throw new Error(`HTTP ${response.status}`);
          }
          console.log('[SSE] Persistent connection established');
          setPersistentConnected(true);
        },

        onmessage(event) {
          if (!event.data?.trim()) return;

          try {
            const data = JSON.parse(event.data);
            // Handle persistent connection events (heartbeats, etc.)
            if (data.type === 'heartbeat') {
              console.log('[SSE] Heartbeat received');
            } else if (data.type === 'connected') {
              console.log('[SSE] Persistent connection confirmed:', data);
            } else {
              // Route other events (like agent_delta) to handleSSEEvent
              // event.event contains the SSE event type (e.g., "agent_delta")
              const eventType = event.event || data.type || 'message';
              console.log(`[SSE] Persistent connection received ${eventType} event:`, data);
              // Debug agent_delta events specifically
              if (eventType === 'agent_delta' || data?.type === 'agent_delta') {
                console.log('[SSE] 🔍 [PERSISTENT CONNECTION] Received agent_delta:', {
                  eventEvent: event.event,
                  eventType,
                  dataType: data?.type,
                  action: data?.action,
                  text: data?.text,
                  fullData: data
                });
              }
              if (handleSSEEventRef.current) {
                handleSSEEventRef.current({ event: eventType, data });
              }
            }
          } catch (error) {
            console.error('[SSE] Parse error in persistent connection:', error);
          }
        },

        onclose() {
          console.log('[SSE] Persistent connection closed');
          setPersistentConnected(false);
        },

        onerror(error) {
          console.error('[SSE] Persistent connection error:', error);
          setPersistentConnected(false);
        }
      });

    } catch (error) {
      console.error('[SSE] Failed to establish persistent connection:', error);
      setPersistentConnected(false);
      if (error instanceof Error && error.message?.includes('401')) {
        handleAuthFailureForToken(connectionToken);
      }
    }
  }, [effectiveCurrentThreadId, handleAuthFailureForToken]);

  // Disconnect from persistent connection only
  const disconnect = useCallback(() => {
    if (persistentAbortControllerRef.current) {
      console.log('[SSE] Disconnecting persistent connection...');
      persistentAbortControllerRef.current.abort();
      persistentAbortControllerRef.current = null;
      setPersistentConnected(false);
    }
  }, []);

  establishPersistentConnectionRef.current = establishPersistentConnection;

  // Auto-connect on mount if enabled; abort superseded attempts when token changes.
  useEffect(() => {
    if (!autoConnect || !token) {
      disconnect();
      return;
    }

    authFailureRef.current = false;
    disconnect();
    void establishPersistentConnectionRef.current?.();

    return () => {
      disconnect();
    };
  }, [autoConnect, token, effectiveCurrentThreadId, disconnect]);

  // Core SSE event handler
  const handleSSEEvent = useCallback((event: SSEEvent) => {
    const { event: eventType, data } = event;

    if (DEBUG_SSE) {
      console.log(`[SSE] ${eventType}:`, data);
    }

    // Thread filter: drop turn events whose thread differs from the active thread
    // (thread switch mid-send, second tab). Thread lifecycle events pass through —
    // they drive thread creation/selection. message_saved has internal guards and
    // feeds the thread controller, so it passes too.
    const eventThreadId = data?.thread_id != null ? String(data.thread_id) : null;
    if (
      eventThreadId &&
      currentThreadIdRef.current &&
      eventThreadId !== currentThreadIdRef.current &&
      eventType !== 'thread_created' &&
      eventType !== 'thread_updated' &&
      eventType !== 'message_saved'
    ) {
      if (DEBUG_SSE) {
        console.log(`[SSE] Dropping ${eventType} for inactive thread ${eventThreadId} (active: ${currentThreadIdRef.current})`);
      }
      return;
    }

    switch (eventType) {
      case 'thread_created':
        {
          const mapped = mapThreadSseEvent(eventType, data);
          if (mapped && threadCallbacks?.onThreadEvent) {
            threadCallbacks.onThreadEvent(mapped);
          } else if (threadCallbacks?.onUIEvent && data?.entity_id) {
            threadCallbacks.onUIEvent({
              entity: 'thread',
              action: 'created',
              entity_id: data.entity_id,
              subject: data.subject
            });
          }
        }
        break;

      case 'content_block_start':
        if (data.content_block) {
          const { index, content_block } = data;
          const { type, id } = content_block;

          // Turn-stream status blocks (tool/voice status messages) carry a server
          // seq stamp and a durable id. They are keyed by id only — never written
          // to the index map, which belongs to generator-allocated blocks (text,
          // ambient memory) and would collide at index 0.
          const isTurnStatusBlock =
            data.seq !== undefined &&
            (type === 'tool' || type === 'voice' || type === 'system' || type === 'memory');
          if (isTurnStatusBlock) {
            const runId = currentRunIdRef.current || 'unknown';
            if (type === 'memory') {
              const deltaMessage = createDeltaMessage('memory', '', {
                id,
                runId,
                orderSeq: data.seq,
                memorySubType: content_block.memory_sub_type,
                memoryLabel: content_block.memory_label,
                memoryCount: content_block.memory_count,
              });
              setMessages(prev =>
                prev.some(m => m.id === id) ? prev : insertDelta(prev, runId, deltaMessage, 'pre')
              );
              break;
            }
            const deltaMessage = createDeltaMessage(type as DeltaType, '', {
              id,
              runId,
              orderSeq: data.seq,
              toolRound: content_block.round,
              toolCallIndex: content_block.call_index,
              toolName: content_block.tool_name,
              toolLabel: content_block.tool_label,
              ...(type === 'tool' ? { toolStatus: 'running' as ToolLifecycleStatus } : {}),
            });
            setMessages(prev =>
              prev.some(m => m.id === id) ? prev : insertDelta(prev, runId, deltaMessage, 'pre')
            );
            break;
          }

          // Ambient memory store blocks (persistent connection, no seq).
          if (type === 'memory' && data.seq === undefined) {
            const runId = currentRunIdRef.current || 'unknown';
            const deltaMessage = createDeltaMessage('memory', '', {
              id,
              runId,
              memorySubType: content_block.memory_sub_type,
              memoryLabel: content_block.memory_label,
              memoryCount: content_block.memory_count,
            });
            setMessages(prev =>
              prev.some(m => m.id === id)
                ? prev
                : insertDelta(
                    prev,
                    runId,
                    deltaMessage,
                    memoryInsertPosition(content_block.memory_sub_type),
                  )
            );
            break;
          }

          // Generator-allocated block: track by index for content_block_delta routing.
          contentBlocksRef.current[index] = {
            id,
            type,
            content: '',
            round: content_block.round,
            call_index: content_block.call_index,
            tool_name: content_block.tool_name,
            tool_label: content_block.tool_label,
          };

          if (type === 'text' || type === 'memory') {
            // Text: defer message creation until first message_delta.
            // Memory: defer until first memory_delta (placement depends on subType).
          } else if (type === 'voice' || type === 'tool' || type === 'system') {
            const runId = currentRunIdRef.current || 'unknown';
            const deltaMessage = createDeltaMessage(type as DeltaType, '', { id, runId });
            setMessages(prev =>
              prev.some(m => m.id === id) ? prev : insertDelta(prev, runId, deltaMessage, 'pre')
            );
          }
        }
        break;
        
      case 'content_block_delta':
        if (data.delta && data.index !== undefined) {
          const { index, delta } = data;
          const { text } = delta;
          const block = contentBlocksRef.current[index];
      
          if (!block) {
            console.warn(`[DELTA] Unknown block #${index}`);
            return;
          }
      
          // Update stored content
          block.content += text || '';
          
          // Route to appropriate UI based on delta type
          switch (delta.type) {
            case 'message_delta':
              // Lazily create assistant message if it doesn't exist yet
              setMessages(prev => {
                const existingMessage = prev.find(msg => msg.id === block.id);
                if (!existingMessage) {
                  console.log(`[MESSAGE DELTA] Creating deferred message for block ${block.id}`);
                  return [...prev, {
                    id: block.id,
                    content: block.content,
                    isUser: false,
                    runId: currentRunIdRef.current || undefined
                  }];
                } else {
                  return prev.map(msg =>
                    msg.id === block.id
                      ? { ...msg, content: block.content }
                      : msg
                  );
                }
              });
              // Set anchor for delta placement in this run
              if (currentRunIdRef.current) {
                runAnchorsRef.current[currentRunIdRef.current] = block.id;
              }
              flushPendingInThreadSuggestions();
              break;

            case 'thinking_delta':
              break;

            case 'voice_delta':
              // Ensure message exists, then update content
              setMessages(prev => {
                const runId = currentRunIdRef.current || 'unknown';
                const withMaybeInsert = ensureDeltaMessageForBlock(prev, runId, block, 'voice');
                return withMaybeInsert.map(msg => {
                  if (msg.id === block.id) {
                    // Preserve delta structure when updating content
                    const updated = { ...msg, content: block.content };
                    // Ensure isDelta and deltaType are preserved
                    if (!updated.isDelta || updated.deltaType !== 'voice') {
                      console.warn(`[VOICE DELTA] Message ${block.id} missing delta structure, fixing...`, {
                        hadIsDelta: updated.isDelta,
                        hadDeltaType: updated.deltaType
                      });
                      updated.isDelta = true;
                      updated.deltaType = 'voice';
                    }
                    return updated;
                  }
                  return msg;
                });
              });

              break;

            case 'memory_delta': {
              setMessages(prev => {
                const runId = currentRunIdRef.current || 'unknown';
                const orderSeq = typeof data.seq === 'number' ? data.seq : undefined;
                const withMaybeInsert = block.id
                  ? ensureMemoryDeltaMessageForBlock(prev, runId, block, orderSeq)
                  : prev;
                return withMaybeInsert.map(msg => {
                  if (msg.id !== block.id) return msg;
                  return {
                    ...msg,
                    content: (msg.content || '') + (block.content || ''),
                    memorySubType: block.memory_sub_type ?? msg.memorySubType,
                    memoryLabel: block.memory_label ?? msg.memoryLabel,
                    memoryCount: block.memory_count ?? msg.memoryCount,
                  };
                });
              });
              break;
            }

            case 'tool_delta':
              // Ensure message exists, then update content
              console.log(`[TOOL DELTA] Received tool_delta for block ${block.id}, content_length=${block.content?.length || 0}`);
              setMessages(prev => {
                const runId = currentRunIdRef.current || 'unknown';
                const withMaybeInsert = ensureDeltaMessageForBlock(prev, runId, block, 'tool');
                return withMaybeInsert.map(msg => {
                  if (msg.id === block.id) {
                    // Preserve delta structure when updating content
                    const updated = { ...msg, content: block.content };
                    // Ensure isDelta and deltaType are preserved
                    if (!updated.isDelta || updated.deltaType !== 'tool') {
                      console.warn(`[TOOL DELTA] Message ${block.id} missing delta structure, fixing...`, {
                        hadIsDelta: updated.isDelta,
                        hadDeltaType: updated.deltaType
                      });
                      updated.isDelta = true;
                      updated.deltaType = 'tool';
                    }
                    return updated;
                  }
                  return msg;
                });
              });
              break;

            case 'tool_complete_delta':
              // Ensure message exists, then update content. Completion summaries
              // are terminal: finalize so the card stack can collapse.
              setMessages(prev => {
                const runId = currentRunIdRef.current || 'unknown';
                const withMaybeInsert = ensureDeltaMessageForBlock(prev, runId, block, 'tool');
                return withMaybeInsert.map(msg =>
                  msg.id === block.id
                    ? {
                        ...msg,
                        content: block.content,
                        toolStatus: msg.toolStatus ?? 'success',
                        finalizedAt: msg.finalizedAt ?? Date.now(),
                      }
                    : msg
                );
              });
              break;

            case 'system_delta':
              // Ensure message exists, then update content
              setMessages(prev => {
                const runId = currentRunIdRef.current || 'unknown';
                const withMaybeInsert = ensureDeltaMessageForBlock(prev, runId, block, 'system');
                return withMaybeInsert.map(msg =>
                  msg.id === block.id
                    ? { ...msg, content: block.content }
                    : msg
                );
              });
              break;

            case 'ui_delta':
              // Ambient UI events (cache invalidation hints, thread lifecycle).
              // Navigation/content-update messaging now rides the turn stream
              // via agent_delta — no message rows are created here.
              if (delta.ui_event) {
                const uiEvent = delta.ui_event;
                const mappedThreadEvent = mapThreadSseEvent('ui_delta', uiEvent);
                if (mappedThreadEvent && threadCallbacks?.onThreadEvent) {
                  threadCallbacks.onThreadEvent(mappedThreadEvent);
                }
                threadCallbacks?.onUIEvent?.(uiEvent);
              }
              break;

            case 'search_thinking_delta':
              // Handle search thinking events
              if (delta.search_event && searchCallbacks?.onSearchThinking) {
                searchCallbacks.onSearchThinking(delta.search_event);
              }
              break;

            case 'search_progress_delta':
              // Handle search progress events
              if (delta.search_event && searchCallbacks?.onSearchProgress) {
                searchCallbacks.onSearchProgress(delta.search_event);
              }
              break;

            case 'search_candidate_delta':
              // Handle search candidate found events
              if (delta.search_event && searchCallbacks?.onSearchCandidate) {
                searchCallbacks.onSearchCandidate(delta.search_event);
              }
              break;

            case 'search_stage_delta':
              // Handle search stage events (alias for search_progress)
              if (delta.search_event && searchCallbacks?.onSearchProgress) {
                searchCallbacks.onSearchProgress(delta.search_event);
              }
              break;

            default:
              console.log(`[DELTA] Unknown type: ${delta.type}`);
          }
        }
        break;
        
      case 'content_block_stop': {
        // Status blocks (turn stream / ambient) carry content_block with a durable
        // id — prefer it over the index map, which only serves generator blocks.
        let block: { id: string; type: string } | undefined;
        if (data.content_block) {
          block = { id: data.content_block.id, type: data.content_block.type };
        } else if (data.index !== undefined) {
          block = contentBlocksRef.current[data.index];
        }

        if (block && (block.type === 'voice' || block.type === 'memory' || block.type === 'tool' || block.type === 'system')) {
          const blockId = block.id;
          const blockType = block.type;
          setMessages(prev => prev.map(msg => {
            if (msg.id !== blockId) return msg;
            const finalized = { ...msg, finalizedAt: Date.now() };
            if (!finalized.isDelta || finalized.deltaType !== blockType) {
              finalized.isDelta = true;
              finalized.deltaType = blockType as DeltaType;
            }
            return finalized;
          }));
        }
        break;
      }
        
      case 'message_start':
        // Server mints a stable turn id per POST and emits it as the first turn
        // event (run_id) — no provisional client-side id needed.
        currentRunIdRef.current = null;

        // Fallback: promote thread_id from stream events when direct thread_created is missed.
        // Controller guards prevent duplicate/invalid lifecycle transitions.
        {
          const mapped = mapThreadSseEvent('message_start', data);
          if (mapped && threadCallbacks?.onThreadEvent) {
            threadCallbacks.onThreadEvent(mapped);
          }
        }

        if (onMessageStart) {
          onMessageStart();
        }
        break;

      case 'run_id':
        if (data.runId) {
          currentRunIdRef.current = String(data.runId);
        }
        break;

      case 'message_stop':
        if (DEBUG_SSE) {
          console.log('[MESSAGE STOP]');
        }
        setIsLoading(false);
        resetMessageSendState();
        messageStreamActiveRef.current = false;
        break;


      case 'message_saved':
        console.log('🔄 [SSE THREAD] Message saved event received:', {
          thread_id: data.thread_id,
          message_id: data.message_id,
          message_type: data.message_type,
          content_length: data.content?.length || 0
        });
        if (
          data.message_type === 'assistant' &&
          data.message_id &&
          (!data.thread_id || !effectiveCurrentThreadId || String(data.thread_id) === String(effectiveCurrentThreadId))
        ) {
          const savedMessageId = String(data.message_id);
          setMessages((prev) => {
            const runId = currentRunIdRef.current;
            const anchorId = runId ? runAnchorsRef.current[runId] : undefined;
            let targetIdx = anchorId ? prev.findIndex((m) => m.id === anchorId) : -1;
            if (targetIdx < 0) {
              for (let i = prev.length - 1; i >= 0; i--) {
                const m = prev[i];
                if (!m.isUser && !m.isDelta) {
                  targetIdx = i;
                  break;
                }
              }
            }
            if (targetIdx < 0) return prev;
            const next = [...prev];
            next[targetIdx] = { ...next[targetIdx], serverMessageId: savedMessageId };
            return next;
          });
          flushPendingInThreadSuggestions();
        }
        // Do not synthesize thread_created from message_saved; this can cause navigation churn on send.
        // Keep message_saved as an optional side-channel callback only.
        if (threadCallbacks?.onMessageSaved) {
          console.log('🔄 [SSE THREAD] Calling onMessageSaved callback');
          threadCallbacks.onMessageSaved(data);
        } else {
          console.log('🔄 [SSE THREAD] No onMessageSaved callback registered');
        }
        break;

      case 'turn_text_complete': {
        setIsLoading(false);
        resetMessageSendState();
        // Safety sweep: the turn's text is final, so any tool card still
        // "running" missed its tool_result (reconnect, dropped event).
        // Finalize as success so the activity stack can collapse.
        const sweepRunId = currentRunIdRef.current;
        if (sweepRunId) {
          setMessages(prev => prev.map(msg => {
            if (
              msg.runId !== sweepRunId ||
              !msg.isDelta ||
              msg.deltaType !== 'tool' ||
              msg.toolStatus
            ) {
              return msg;
            }
            return {
              ...msg,
              toolStatus: 'success' as ToolLifecycleStatus,
              finalizedAt: msg.finalizedAt ?? Date.now(),
            };
          }));
        }
        break;
      }

      case 'suggestion_block_start':
      case 'suggestion_delta':
      case 'suggestion_chip_ready':
      case 'suggestion_block_stop':
        applySuggestionStreamEvent(
          eventType,
          data as Record<string, unknown>,
          getSuggestionStreamHandlers(),
        );
        break;

      case 'capability_menu_stream_start':
      case 'capability_category_start':
      case 'capability_example_delta':
      case 'capability_example_ready':
      case 'capability_category_stop':
      case 'capability_menu_stream_stop':
        applyCapabilityMenuStreamEvent(
          eventType,
          data as Record<string, unknown>,
          getCapabilityMenuStreamHandlers(),
        );
        // Structural events publish immediately (deltas already applied to the ref
        // in order); per-token delta/ready events ride the rAF-batched flush.
        if (eventType !== 'capability_example_delta' && eventType !== 'capability_example_ready') {
          flushCapabilityMenu();
        }
        break;

      case 'chat_suggestions':
        applyChatSuggestionsPayload(data as ChatSuggestionsPayload);
        break;

      case 'message_delta':
        // Message deltas are normal, no need to log as unhandled
        break;

      case 'agent_delta': {
        if (data?.action === 'llm_provider_notice') {
          const msg = typeof data.message === 'string' ? data.message : '';
          const code = typeof data.notice_code === 'string' ? data.notice_code : '';
          const key = `${code}:${msg}`;
          if (msg && !llmProviderNoticeDedupeRef.current.has(key)) {
            llmProviderNoticeDedupeRef.current.add(key);
            toast.warning(msg, { duration: 8000 });
          }
          break;
        }
        // Handle agent capability changes (e.g., tools changed, summary streaming, navigation, content updates)
        // Throttle tools_changed and summary_* when document recently became visible (tab return) to avoid capabilities reload
        const visibilityThrottleMs = 3000;
        const lastVisible = lastVisibleAtRef.current;
        const recentlyVisible = lastVisible > 0 && Date.now() - lastVisible < visibilityThrottleMs;
        const shouldProcessCapability = shouldProcessCapabilityDelta(
          data?.action,
          recentlyVisible,
          suppressCapabilityUpdatesUntilNextSendRef.current,
          data?.focus_changed === true,
        );
        if (!shouldProcessCapability) {
          console.log('[SSE] 🤖 Skipping agent_delta capability event:', {
            action: data?.action,
            recentlyVisible,
            suppressUntilNextSend: suppressCapabilityUpdatesUntilNextSendRef.current,
          });
          break;
        }

        console.log('[SSE] 🤖 RECEIVED agent_delta event:', data);
        console.log('[SSE] 🔍 [AGENT DELTA] Full event structure:', {
          eventType,
          dataType: data?.type,
          action: data?.action,
          target: data?.target,
          entity: data?.entity,
          cache_keys: data?.cache_keys,
          fullData: data
        });
        
        if (data.action === 'tools_changed') {
          console.log('[AGENT DELTA] 🔧 Tools changed:', data.readable_tool_names);
          if (data.readable_tool_names) {
            setAgentCapabilities(data.readable_tool_names);
          }
          const menu = data.capability_menu;
          if (menu?.categories && Array.isArray(menu.categories)) {
            applyCapabilityMenuShells(
              menu.menu_revision as string | undefined,
              menu.categories as import('@/types/composer').ComposerCapabilityCategory[],
              getCapabilityMenuStreamHandlers(),
            );
            // Shell replacement is structural — publish immediately.
            flushCapabilityMenu();
          }
        } else if (data.action === 'navigation') {
          // Handle navigation delta
          console.log('[AGENT DELTA] 🧭 Navigation delta:', data);
          const target = data.target;
          const message = data.message;
          const eventRunId = data.run_id || data.runId || null;
          const allowNavigation = shouldApplyNavigationDelta({
            target,
            recentlyVisible,
            messageStreamActive: messageStreamActiveRef.current,
            eventRunId,
            currentRunId: currentRunIdRef.current,
          });
          
          console.log('[AGENT DELTA] 🧭 Navigation details:', {
            target,
            message,
            allowNavigation,
            recentlyVisible,
            messageStreamActive: messageStreamActiveRef.current,
            eventRunId,
            currentRunId: currentRunIdRef.current,
            hasNavigate: !!navigationHelpers?.navigate,
            hasSearchParams: !!navigationHelpers?.searchParams,
            navigationHelpers: navigationHelpers ? Object.keys(navigationHelpers) : null
          });
          
          if (!allowNavigation) {
            console.log('[AGENT DELTA] 🧭 Skipping navigation delta due to guard rules');
          } else if (target && navigationHelpers?.navigate && navigationHelpers?.searchParams) {
            const now = Date.now();
            const lastNavigation = lastNavigationRef.current;
            const isDuplicate = isDuplicateNavigationTarget(
              lastNavigation?.target ?? null,
              lastNavigation?.at ?? 0,
              target,
              now
            );
            if (isDuplicate) {
              console.log('[AGENT DELTA] 🧭 Skipping duplicate navigation target:', target);
            } else {
              lastNavigationRef.current = { target, at: now };
              const isMobile = !!navigationHelpers?.isMobile;
              const mobileBehavior = normalizeNavigationMobileBehavior(data.mobile_behavior);
              const shouldAutoOpen =
                data.auto_open === false
                  ? false
                  : shouldAutoOpenNavigationIntent(isMobile, mobileBehavior, data.auto_open);
              const delayMs = clampNavigationDelay(data.delay_ms, isMobile);
              const messageId = generateId();
              const createdAt = Date.now();
              const navigationMessage = {
                id: messageId,
                content: '',
                isUser: false,
                runId: eventRunId || currentRunIdRef.current || 'unknown',
                messageType: 'navigation_intent',
                orderSeq: typeof data.seq === 'number' ? data.seq : undefined,
                navigationIntent: {
                  target_path: target,
                  label: data.label,
                  reason: message,
                  focus_key: data.focus_key,
                  delay_ms: delayMs,
                  mobile_behavior: mobileBehavior,
                  status: 'pending',
                  createdAt,
                  opensAt: shouldAutoOpen ? createdAt + delayMs : undefined,
                },
              } as ChatMessage;

              setMessages(prev => {
                const updated = prev.map(msg => {
                  if (
                    msg.messageType === 'navigation_intent' &&
                    msg.navigationIntent &&
                    msg.navigationIntent.status === 'pending'
                  ) {
                    clearNavigationTimer(msg.id);
                    const intent = msg.navigationIntent;
                    return {
                      ...msg,
                      navigationIntent: {
                        ...intent,
                        status: 'cancelled' as const,
                        opensAt: undefined,
                      },
                    } as ChatMessage;
                  }
                  return msg;
                });
                return insertDelta(
                  updated,
                  navigationMessage.runId || 'unknown',
                  navigationMessage,
                  'pre'
                );
              });

              if (shouldAutoOpen) {
                scheduleNavigationIntent(navigationMessage);
              }
            }
          } else {
            console.warn('[AGENT DELTA] ⚠️ Navigation requested but navigate function or searchParams not available', {
              target,
              hasNavigate: !!navigationHelpers?.navigate,
              hasSearchParams: !!navigationHelpers?.searchParams
            });
          }
        } else if (data.action === 'content_update') {
          // Handle content update delta
          console.log('[AGENT DELTA] 🔄 Content update delta:', data);
          const { entity, entity_id, cache_keys, message } = data;
          
          console.log('[AGENT DELTA] 🔄 Content update details:', {
            entity,
            entity_id,
            cache_keys,
            message,
            hasQueryClient: !!navigationHelpers?.queryClient,
            cache_keys_type: Array.isArray(cache_keys) ? 'array' : typeof cache_keys
          });
          
          // Invalidate cache keys
          if (cache_keys && navigationHelpers?.queryClient) {
            const keysArray = Array.isArray(cache_keys) ? cache_keys : [cache_keys];
            keysArray.forEach((key: string) => {
              console.log(`[AGENT DELTA] 🔄 Invalidating cache key: ${key}`);
              // React Query's invalidateQueries matches queries that start with the provided key
              // So ['evaluations'] will match ['evaluations', profileId, opportunityId]
              // And ['my-applications'] will match ['my-applications'] exactly
              navigationHelpers.queryClient.invalidateQueries({ queryKey: [key] });
            });
          } else {
            console.warn('[AGENT DELTA] ⚠️ Content update requested but cache_keys or queryClient not available', {
              cache_keys,
              hasQueryClient: !!navigationHelpers?.queryClient
            });
          }
          
          // Display update message as delta if provided
          if (message) {
            const messageKey = `update_${entity}_${entity_id}`;
            const now = Date.now();
            const recent = recentNavMessagesRef.current.get(messageKey);
            if (!recent || now - recent > 5000) {
              recentNavMessagesRef.current.set(messageKey, now);
              const updateDelta = createDeltaMessage('content_update', message, {
                runId: data.run_id || data.runId || undefined,
                orderSeq: typeof data.seq === 'number' ? data.seq : undefined,
              });
              setMessages(prev => insertDelta(prev, updateDelta.runId || 'unknown', updateDelta, 'pre'));
            }
          }
        } else if (data.action === 'content_refresh') {
          // Handle content refresh delta
          console.log('[AGENT DELTA] 🔃 Content refresh delta:', data);
          const { cache_keys, message } = data;
          
          // Refresh cache keys
          if (cache_keys && navigationHelpers?.queryClient) {
            cache_keys.forEach((key: string) => {
              console.log(`[AGENT DELTA] 🔃 Refreshing cache key: ${key}`);
              navigationHelpers.queryClient.invalidateQueries({ queryKey: [key] });
            });
          }
          
          // Display refresh message as delta if provided
          if (message) {
            const messageKey = `refresh_${cache_keys?.join('_')}`;
            const now = Date.now();
            const recent = recentNavMessagesRef.current.get(messageKey);
            if (!recent || now - recent > 5000) {
              recentNavMessagesRef.current.set(messageKey, now);
              const refreshDelta = createDeltaMessage('content_refresh', message, {
                runId: data.run_id || data.runId || undefined,
                orderSeq: typeof data.seq === 'number' ? data.seq : undefined,
              });
              setMessages(prev => insertDelta(prev, refreshDelta.runId || 'unknown', refreshDelta, 'pre'));
            }
          }
        } else if (data.action === 'object_update') {
          const eventRunId = data.run_id || data.runId || currentRunIdRef.current || 'unknown';
          let itemsRaw: Record<string, unknown>[] = [];
          if (Array.isArray(data.items) && data.items.length > 0) {
            itemsRaw = data.items as Record<string, unknown>[];
          } else {
            itemsRaw = [
              {
                object_type: data.object_type,
                object_id: data.object_id,
                object_name: data.object_name,
                object_action: data.object_action || data.action,
                source_tool: data.source_tool,
                object_update_id: data.object_update_id,
                target_path: data.target_path,
                focus_key: data.focus_key,
                changed_fields: data.changed_fields,
                display_label: data.display_label,
              },
            ];
          }
          const items = itemsRaw.map(rawToObjectUpdateItem).filter(it => it.object_type);
          if (navigationHelpers?.queryClient) {
            for (const it of items) {
              for (const queryKey of objectUpdateQueryKeys(String(it.object_type || ''), String(it.object_id || ''))) {
                navigationHelpers.queryClient.invalidateQueries({ queryKey });
              }
            }
          }
          for (const it of items) {
            if (it.object_update_id) {
              recentNavMessagesRef.current.set(`obj_${it.object_update_id}`, Date.now());
            }
          }
          setMessages(prev =>
            appendObjectUpdateItemsToRunGroup(
              prev,
              eventRunId,
              items,
              typeof data.seq === 'number' ? data.seq : undefined,
            )
          );
        } else if (data.action === 'object_update_description') {
          // Merge LLM-generated description into existing object update message
          const objectUpdateId = data.object_update_id;
          const description = data.description;
          if (objectUpdateId && description) {
            setMessages(prev =>
              prev.map(msg => {
                if (msg.messageType !== 'object_update' || !msg.objectUpdate) return msg;
                const ou = msg.objectUpdate;
                if (ou.object_update_id === objectUpdateId) {
                  return {
                    ...msg,
                    objectUpdate: { ...ou, description },
                  };
                }
                const pills = ou.items?.length ? ou.items : objectUpdateToItems(ou);
                let hit = false;
                const nextItems = pills.map(it => {
                  if (it.object_update_id === objectUpdateId) {
                    hit = true;
                    return { ...it, description };
                  }
                  return it;
                });
                if (!hit) return msg;
                return { ...msg, objectUpdate: { items: nextItems } };
              })
            );
          }
        } else if (data.action === 'discourse_support_note') {
          const noteThreadId = data.thread_id != null ? String(data.thread_id) : ''
          const openThreadId = currentThreadIdRef.current
          if (noteThreadId && openThreadId && noteThreadId !== openThreadId) {
            break
          }
          setMessages((prev) =>
            appendDiscourseSupportNote(
              prev,
              { content: data.content, messageId: data.message_id },
              generateId,
            ),
          )
        } else {
          console.log('[AGENT DELTA] ⚠️ Unhandled action:', data.action);
        }
        break;
      }

      case 'tool_delta':
      case 'tool_complete_delta':
      case 'voice_delta':
      case 'memory_delta':
        // Turn-stream / ambient status text chunks (passthrough events carrying content_block).
        if (data.content_block) {
          const block = data.content_block;
          const runId = currentRunIdRef.current || 'unknown';
          const deltaType: DeltaType =
            eventType === 'tool_delta' || eventType === 'tool_complete_delta'
              ? 'tool'
              : eventType === 'voice_delta'
                ? 'voice'
                : 'memory';
          const orderSeq = typeof data.seq === 'number' ? data.seq : undefined;
          const isToolComplete = eventType === 'tool_complete_delta';

          setMessages(prev => {
            const withMaybeInsert =
              deltaType === 'memory'
                ? ensureMemoryDeltaMessageForBlock(prev, runId, block, orderSeq)
                : ensureDeltaMessageForBlock(prev, runId, block, deltaType, orderSeq);
            return withMaybeInsert.map(msg => {
              if (msg.id !== block.id) return msg;
              const updated = {
                ...msg,
                content: isToolComplete
                  ? (block.content || '')
                  : (msg.content || '') + (block.content || ''),
                ...(isToolComplete
                  ? {
                      toolStatus: msg.toolStatus === 'running' ? 'success' : msg.toolStatus,
                      finalizedAt: msg.finalizedAt ?? Date.now(),
                    }
                  : {}),
                ...(deltaType === 'memory'
                  ? {
                      memorySubType: block.memory_sub_type ?? msg.memorySubType,
                      memoryLabel: block.memory_label ?? msg.memoryLabel,
                      memoryCount: block.memory_count ?? msg.memoryCount,
                    }
                  : {}),
              };
              if (!updated.isDelta || updated.deltaType !== deltaType) {
                updated.isDelta = true;
                updated.deltaType = deltaType;
              }
              return updated;
            });
          });
        }
        break;

      case 'tool_result': {
        // Per-tool lifecycle completion from tools_node: close the matching tool
        // card (keyed by round/call_index) as success or error.
        const resultRound = typeof data.round === 'number' ? data.round : undefined;
        const resultCall = typeof data.call_index === 'number' ? data.call_index : undefined;
        const resultStatus: ToolLifecycleStatus = data.status === 'error' ? 'error' : 'success';
        const runId = currentRunIdRef.current || 'unknown';
        setMessages(prev => prev.map(msg => {
          if (
            msg.runId !== runId ||
            !msg.isDelta ||
            msg.deltaType !== 'tool' ||
            msg.toolRound !== resultRound ||
            msg.toolCallIndex !== resultCall
          ) {
            return msg;
          }
          return {
            ...msg,
            toolStatus: resultStatus,
            toolError: typeof data.error === 'string' ? data.error : undefined,
            finalizedAt: msg.finalizedAt ?? Date.now(),
          };
        }));
        break;
      }

      default:
        if (DEBUG_SSE) {
          console.log(`[SSE] Unhandled event: ${eventType}`);
        }
    }
  }, [
    resetMessageSendState,
    navigationHelpers,
    clearNavigationTimer,
    scheduleNavigationIntent,
    applyChatSuggestionsPayload,
    refreshChatSuggestions,
    scheduleSuggestionsAwaitTimeout,
    effectiveCurrentThreadId,
    getSuggestionStreamHandlers,
    getCapabilityMenuStreamHandlers,
    flushCapabilityMenu,
  ]);

  const revokeAttachmentPreview = (att: ThreadMessageAttachment) => {
    if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
  };

  const uploadThreadAttachment = useCallback(async (file: File): Promise<boolean> => {
    const ensure = navigationHelpers?.ensureThread;
    if (!ensure) {
      console.warn('[ATTACHMENTS] ensureThread not configured');
      return false;
    }
    const tid = await ensure();
    if (!tid) {
      console.warn('[ATTACHMENTS] No thread id for upload');
      return false;
    }
    const localId = `local:${crypto.randomUUID()}`;
    // Instant thumbnail for image drops/pastes — object URL lives until the chip is unstaged.
    // Clipboard files may have empty File.type; isImageLikeFile also checks extension.
    const previewUrl = isImageLikeFile(file) ? URL.createObjectURL(file) : undefined;
    const optimistic: ThreadMessageAttachment = {
      id: localId,
      original_name: file.name,
      mime_type: file.type || (isImageLikeFile(file) ? 'image/png' : 'application/octet-stream'),
      status: 'pending',
      isUploading: true,
      previewUrl,
    };
    stagedAttachmentsRef.current = [...stagedAttachmentsRef.current, optimistic];
    setStagedAttachments([...stagedAttachmentsRef.current]);
    lastAttachmentThreadIdRef.current = tid;

    const fd = new FormData();
    fd.append('file', file);
    const headers: Record<string, string> = {};
    if (tokenRef.current) headers.Authorization = `Bearer ${tokenRef.current}`;
    try {
      const r = await fetch(`/api/threads/${tid}/attachments`, { method: 'POST', headers, body: fd });
      const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
      if (!r.ok) {
        console.error('[ATTACHMENTS] Upload failed', r.status, data);
        const errMsg =
          typeof data.error === 'string'
            ? data.error
            : typeof data.detail === 'string'
              ? data.detail
              : `Upload failed (${r.status})`;
        stagedAttachmentsRef.current = stagedAttachmentsRef.current.map((a) =>
          a.id === localId
            ? {
                ...a,
                isUploading: false,
                status: 'failed',
                error: errMsg,
              }
            : a
        );
        setStagedAttachments([...stagedAttachmentsRef.current]);
        return false;
      }
      const row: ThreadMessageAttachment = {
        id: String(data.id),
        original_name: (data.original_name as string) ?? file.name,
        mime_type: (data.mime_type as string) || file.type || 'application/octet-stream',
        status: (data.status as string) ?? 'pending',
        file_url: data.file_url as string | undefined,
        size_bytes: data.size_bytes as number | undefined,
        error: (data.error as string | null | undefined) ?? null,
        isUploading: false,
        previewUrl,
      };
      stagedAttachmentsRef.current = stagedAttachmentsRef.current.map((a) =>
        a.id === localId ? row : a
      );
      setStagedAttachments([...stagedAttachmentsRef.current]);
      return true;
    } catch (e) {
      console.error('[ATTACHMENTS] Upload error', e);
      stagedAttachmentsRef.current = stagedAttachmentsRef.current.map((a) =>
        a.id === localId
          ? {
              ...a,
              isUploading: false,
              status: 'failed',
              error: e instanceof Error ? e.message : 'Upload failed',
            }
          : a
      );
      setStagedAttachments([...stagedAttachmentsRef.current]);
      return false;
    }
  }, [navigationHelpers]);

  const removeStagedAttachment = useCallback((id: string) => {
    for (const a of stagedAttachmentsRef.current) {
      if (a.id === id) revokeAttachmentPreview(a);
    }
    stagedAttachmentsRef.current = stagedAttachmentsRef.current.filter((a) => a.id !== id);
    setStagedAttachments([...stagedAttachmentsRef.current]);
  }, []);

  // Send message function
  const sendMessage = useCallback((message: string, threadId?: string): boolean => {
    console.log('🚀 [SEND MESSAGE] sendMessage function called with threadId:', threadId);
    console.log('🚨 [USE SSE] sendMessage called with:', {
      message: message.substring(0, 30) + (message.length > 30 ? '...' : ''),
      threadId: threadId,
      effectiveCurrentThreadId: effectiveCurrentThreadId,
      threadIdIsUndefined: threadId === undefined,
      threadIdIsNull: threadId === null,
      threadIdIsEmptyString: threadId === '',
      willUseCurrentThreadId: !threadId && effectiveCurrentThreadId,
      finalThreadId: threadId || effectiveCurrentThreadId,
      callStack: new Error().stack?.split('\n').slice(0, 5).join('\n')
    });
    console.log('🚨 [THREAD DEBUG] sendMessage() called with:', {
      message: message.substring(0, 30) + (message.length > 30 ? '...' : ''),
      threadId: threadId,
      threadIdType: typeof threadId,
      threadIdTruthy: !!threadId,
      effectiveCurrentThreadId: effectiveCurrentThreadId
    });
    if (!message.trim() && !stagedAttachmentsRef.current.length) {
      return false;
    }
    if (stagedAttachmentsRef.current.some((a) => a.isUploading)) {
      console.log('[SEND MESSAGE] Blocked: attachment upload in progress');
      return false;
    }
    if (messageSendInFlightRef.current) {
      console.log('[SEND MESSAGE] Ignoring duplicate submit while send is in-flight');
      return false;
    }
    if (authFailureRef.current) return false;
    if (tokenRef.current && isJwtExpired(tokenRef.current)) {
      handleAuthFailure();
      return false;
    }
    messageSendInFlightRef.current = true;
    suppressCapabilityUpdatesUntilNextSendRef.current = false;
    clearChatSuggestions();
    messageSendFailsafeTimeoutRef.current = window.setTimeout(() => {
      console.warn('[SEND MESSAGE] Failsafe timeout reached, clearing in-flight send state');
      resetMessageSendState();
    }, 30000);

    // Drop previewUrl from the message snapshot — object URLs are revoked below when unstaging.
    const attachSnap = stagedAttachmentsRef.current.map((a) => {
      const copy = { ...a };
      delete copy.previewUrl;
      return copy;
    });
    const attachmentIds = attachSnap
      .filter((a) => !a.isUploading && !a.id.startsWith('local:'))
      .map((a) => a.id);
    // Prefer explicit threadId, then React state, then upload thread (state may lag behind selectThread after file upload).
    let finalThreadId =
      (threadId && String(threadId)) || effectiveCurrentThreadId || null;
    if (!finalThreadId && attachmentIds.length > 0 && lastAttachmentThreadIdRef.current) {
      finalThreadId = lastAttachmentThreadIdRef.current;
      console.log(
        '[THREAD FIX] Using lastAttachmentThreadIdRef after upload (React thread id not ready yet):',
        finalThreadId
      );
    }
    console.log('🚨 [THREAD FIX] Using finalThreadId:', finalThreadId, 'original threadId:', threadId, 'effectiveCurrentThreadId:', effectiveCurrentThreadId);

    // Don't disconnect persistent connection - they can coexist
    // The persistent connection handles agent deltas, message stream handles chat
    // If we need to disconnect, do it after message stream completes

    stagedAttachmentsRef.current.forEach(revokeAttachmentPreview);
    stagedAttachmentsRef.current = [];
    setStagedAttachments([]);

    // Add user message immediately
    const displayContent = message.trim() || '';
    const userMessage: ChatMessage = {
      id: generateId(),
      content: displayContent,
      isUser: true,
      ...(attachSnap.length ? { attachments: attachSnap } : {}),
    };
    setMessages(prev => [...prev, userMessage]);

    // Clean up any existing message stream connection
    if (messageAbortControllerRef.current) {
      console.log('[SSE] Cleaning up existing message stream connection');
      messageAbortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    messageAbortControllerRef.current = abortController;
    
    // Clear generator content block index map for the new turn
    contentBlocksRef.current = {};

    setIsLoading(true);
    setIsConnected(true);
    messageStreamActiveRef.current = true; // Mark message stream as active

    console.log('[SSE] Sending message:', {
      message: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
      threadId,
      currentThreadId,
      final_thread_id: finalThreadId,
    });

    const connectionToken = tokenRef.current;

    void fetchEventSource(SSE_ENDPOINT, {
      method: 'POST',
      openWhenHidden: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...(connectionToken && { 'Authorization': `Bearer ${connectionToken}` }),
      },
      body: JSON.stringify({
        message,
        ...(finalThreadId && { thread_id: finalThreadId }),
        ...(attachmentIds.length > 0 && { attachment_ids: attachmentIds }),
        ...employerAgentSessionContext(),
      }),
      signal: abortController.signal,
      
      onopen(response) {
        if (!response.ok) {
          if (response.status === 401) {
            handleAuthFailureForToken(connectionToken);
          }
          throw new Error(`HTTP ${response.status}`);
        }
        console.log('[SSE] Connection opened');
        return Promise.resolve();
      },
      
      onmessage(event) {
        if (!event.data?.trim()) return;

        try {
          const data = JSON.parse(event.data);
          // Route through the ref so long-lived streams use the freshest handler
          // (thread filter, callbacks) rather than the closure captured at send time.
          (handleSSEEventRef.current ?? handleSSEEvent)({ event: event.event, data });
        } catch (error) {
          console.error('[SSE] Parse error:', error);
        }
      },
      
      onclose() {
        console.log('[SSE] Message stream connection closed');
        setIsConnected(false);
        setIsLoading(false);
        resetMessageSendState();
        
        // Clean up message stream controller
        messageAbortControllerRef.current = null;
        
        // Reconnect persistent connection after message stream completes (if needed, skip if auth failed)
        if (autoConnect && tokenRef.current && !isPersistentConnected && !authFailureRef.current) {
          console.log('[SSE] Reconnecting persistent connection after message stream');
          setTimeout(() => {
            establishPersistentConnection();
          }, 100);
        }
      },
      
      onerror(error) {
        console.error('[SSE] Message stream error:', error);
        setIsConnected(false);
        setIsLoading(false);
        resetMessageSendState();
        messageAbortControllerRef.current = null;
        
        if (autoConnect && tokenRef.current && !isPersistentConnected && !authFailureRef.current) {
          console.log('[SSE] Reconnecting persistent connection after message stream error');
          setTimeout(() => {
            establishPersistentConnection();
          }, 100);
        }
        throw error instanceof Error ? error : new Error('Message stream failed');
      }
    }).catch((error) => {
      console.error('[SEND] Error starting message stream:', error);
      setIsConnected(false);
      setIsLoading(false);
      resetMessageSendState();
      messageAbortControllerRef.current = null;

      if (error instanceof Error && error.message?.includes('401')) {
        handleAuthFailureForToken(connectionToken);
      } else if (autoConnect && tokenRef.current && !isPersistentConnected && !authFailureRef.current) {
        console.log('[SSE] Reconnecting persistent connection after send error');
        setTimeout(() => {
          establishPersistentConnection();
        }, 100);
      }
    });
    return true;
  }, [
    handleSSEEvent,
    autoConnect,
    isPersistentConnected,
    disconnect,
    establishPersistentConnection,
    resetMessageSendState,
    clearChatSuggestions,
    handleAuthFailure,
    effectiveCurrentThreadId,
  ]);

  const clearCapabilityMenu = useCallback(() => {
    if (capabilityMenuFlushRafRef.current) {
      cancelAnimationFrame(capabilityMenuFlushRafRef.current);
      capabilityMenuFlushRafRef.current = 0;
    }
    capabilityMenuRevisionRef.current = null;
    capabilityMenuCategoriesRef.current = [];
    setCapabilityMenuCategories([]);
  }, []);

  // Function to start a new conversation
  const startNewConversation = useCallback(() => {
    console.log('🚨 [THREAD DEBUG] startNewConversation() called!');
    // Clear local state - thread ID is managed by parent component
    clearAllNavigationTimers();
    setMessages([]);
    currentRunIdRef.current = null;
    clearChatSuggestions();
    stagedAttachmentsRef.current.forEach(revokeAttachmentPreview);
    stagedAttachmentsRef.current = [];
    setStagedAttachments([]);
    lastAttachmentThreadIdRef.current = null;
    console.log('[THREAD] Started new conversation (thread ID managed by parent)');
  }, [clearAllNavigationTimers, clearChatSuggestions]);

  // Update handleSSEEvent ref when handleSSEEvent changes
  useEffect(() => {
    handleSSEEventRef.current = handleSSEEvent;
  }, [handleSSEEvent]);

  const cancelActiveMessage = useCallback(() => {
    if (!messageAbortControllerRef.current) {
      setIsLoading(false);
      resetMessageSendState();
      return;
    }
    console.log('[SSE] Cancelling active message stream');
    messageAbortControllerRef.current.abort();
    messageAbortControllerRef.current = null;
    setIsLoading(false);
    setIsConnected(false);
    resetMessageSendState();
  }, [resetMessageSendState]);

  // Stable across renders (reads messages via snapshot ref) so memoized message
  // components don't re-render every time the hook re-runs.
  const openNavigationIntent = useCallback((messageId: string) => {
    const msg = messagesSnapshotRef.current.find(m => m.id === messageId);
    const intent = msg?.navigationIntent;
    if (!intent) return;
    performNavigationIntent(messageId, intent.target_path, intent.mobile_behavior ?? 'prompt', true);
  }, [performNavigationIntent]);

  return {
    // State
    messages,
    isConnected,
    isPersistentConnected,
    isLoading,
    agentCapabilities,
    capabilityMenuCategories,
    clearCapabilityMenu,
    currentThreadId,
    stagedAttachments,
    chatSuggestions,
    isChatSuggestionsLoading,
    isAwaitingChatSuggestions,

    // Actions
    sendMessage,
    cancelActiveMessage,
    startNewConversation,
    clearChatSuggestions,
    refreshChatSuggestions,
    loadSuggestionsForThread,
    setMessages,
    setAgentCapabilities: setAgentCapabilities,
    openNavigationIntent,
    cancelNavigationIntent,
    uploadThreadAttachment,
    removeStagedAttachment,
  };
};
